/**
 * GRI/CSRD Module - Double Materiality Assessment
 * EU CSRD Compliance & Global GRI Reporting
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TrendingUp, AlertCircle, CheckCircle, Save, RefreshCw,
  BarChart3, Leaf, Users, Shield, Filter, Grid3x3, X, Download
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const GRICSRDModule = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [topics, setTopics] = useState([]);
  const [assessment, setAssessment] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [scores, setScores] = useState({});
  const [activeView, setActiveView] = useState('assessment'); // 'assessment' or 'matrix'
  const [filterCategory, setFilterCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (study?.id) {
      fetchData();
    }
  }, [study?.id]);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [topicsRes, assessmentRes, matrixRes] = await Promise.all([
        axios.get(`${API}/api/gri-csrd/${study.id}/topics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/gri-csrd/${study.id}/materiality-assessment`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/gri-csrd/${study.id}/materiality-matrix`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setTopics(topicsRes.data.topics || []);
      setAssessment(assessmentRes.data);
      setMatrix(matrixRes.data);

      // Initialize scores from saved assessment
      const initialScores = {};
      if (assessmentRes.data.scores) {
        assessmentRes.data.scores.forEach(s => {
          initialScores[s.topic_id] = {
            impact: s.impact_score,
            financial: s.financial_score,
            notes: s.notes || ''
          };
        });
      }
      setScores(initialScores);
    } catch (error) {
      console.error('Error fetching GRI/CSRD data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScoreChange = (topicId, type, value) => {
    setScores(prev => ({
      ...prev,
      [topicId]: {
        ...prev[topicId],
        [type]: parseInt(value)
      }
    }));
  };

  const saveAssessment = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const scoresArray = Object.entries(scores)
        .filter(([_, s]) => s.impact && s.financial)
        .map(([topicId, s]) => ({
          topic_id: topicId,
          impact_score: s.impact,
          financial_score: s.financial,
          notes: s.notes || null
        }));

      const res = await axios.post(`${API}/api/gri-csrd/${study.id}/materiality-assessment`, {
        scores: scoresArray
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showSuccess(`Assessment saved! ${res.data.statistics.material_topics} topics marked as material.`);
      fetchData(); // Refresh to get updated matrix
    } catch (error) {
      showError('Error saving assessment: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const getScoreLabel = (score) => {
    if (score === 5) return 'Very High';
    if (score === 4) return 'High';
    if (score === 3) return 'Medium';
    if (score === 2) return 'Low';
    if (score === 1) return 'Very Low';
    return 'Not Rated';
  };

  const getScoreColor = (score) => {
    if (score >= 4) return 'bg-red-100 text-red-700 border-red-300';
    if (score === 3) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  const getCategoryIcon = (category) => {
    if (category === 'Environmental') return <Leaf className="w-5 h-5 text-green-600" />;
    if (category === 'Social') return <Users className="w-5 h-5 text-blue-600" />;
    return <Shield className="w-5 h-5 text-purple-600" />;
  };

  const filteredTopics = filterCategory === 'All' 
    ? topics 
    : topics.filter(t => t.category === filterCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Loading GRI/CSRD assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">GRI / CSRD Double Materiality Assessment</h2>
            <p className="text-indigo-100">EU Corporate Sustainability Reporting Directive (CSRD) Compliance</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                const token = localStorage.getItem('token');
                window.open(`${API}/api/gri-csrd/${study.id}/export/pdf-report?token=${token}`, '_blank');
              }}
              className="px-4 py-2 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Report
            </button>
            <button
              onClick={() => setActiveView('assessment')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeView === 'assessment' ? 'bg-white text-indigo-600' : 'bg-indigo-500 text-white hover:bg-indigo-400'
              }`}
            >
              <Grid3x3 className="w-4 h-4 inline mr-2" />
              Assessment
            </button>
            <button
              onClick={() => setActiveView('matrix')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeView === 'matrix' ? 'bg-white text-indigo-600' : 'bg-indigo-500 text-white hover:bg-indigo-400'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Matrix
            </button>
          </div>
        </div>
      </div>

      {/* Assessment Progress */}
      {assessment && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Assessment Progress</h3>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {assessment.scores?.length || 0} / {topics.length} topics assessed
              </span>
              {assessment.completed && (
                <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                  <CheckCircle className="w-4 h-4" /> Complete
                </span>
              )}
            </div>
          </div>
          {assessment.statistics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-600">Material Topics</p>
                <p className="text-2xl font-bold text-blue-900">{assessment.statistics.material_topics}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <p className="text-sm text-red-600">High Priority</p>
                <p className="text-2xl font-bold text-red-900">{assessment.statistics.high_priority_topics}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600">Materiality Rate</p>
                <p className="text-2xl font-bold text-green-900">{assessment.statistics.materiality_rate}%</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <p className="text-sm text-purple-600">Total Topics</p>
                <p className="text-2xl font-bold text-purple-900">{assessment.statistics.total_topics_assessed}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Assessment View */}
      {activeView === 'assessment' && (
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-5 h-5 text-gray-600" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="All">All Categories</option>
                <option value="Environmental">Environmental (E1-E5)</option>
                <option value="Social">Social (S1-S4)</option>
                <option value="Governance">Governance (G1)</option>
              </select>
            </div>
            <button
              onClick={saveAssessment}
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Assessment'}
            </button>
          </div>

          {/* Topics Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Topic</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                      Impact on Society/Environment
                      <div className="text-xs font-normal text-gray-500">(1=Low, 5=High)</div>
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                      Financial Impact on Company
                      <div className="text-xs font-normal text-gray-500">(1=Low, 5=High)</div>
                    </th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Codes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTopics.map((topic, index) => {
                    const topicScore = scores[topic.id] || { impact: 0, financial: 0, notes: '' };
                    return (
                      <tr key={topic.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(topic.category)}
                            <div>
                              <div className="font-semibold text-gray-900">{topic.topic}</div>
                              <div className="text-xs text-gray-500">{topic.category}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm text-gray-600">{topic.description}</p>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col items-center gap-2">
                            <select
                              value={topicScore.impact || ''}
                              onChange={(e) => handleScoreChange(topic.id, 'impact', e.target.value)}
                              className={`w-32 px-3 py-2 border-2 rounded-lg font-medium ${
                                topicScore.impact ? getScoreColor(topicScore.impact) : 'border-gray-300'
                              }`}
                            >
                              <option value="">Not Rated</option>
                              <option value="1">1 - Very Low</option>
                              <option value="2">2 - Low</option>
                              <option value="3">3 - Medium</option>
                              <option value="4">4 - High</option>
                              <option value="5">5 - Very High</option>
                            </select>
                            {topicScore.impact > 0 && (
                              <span className="text-xs font-medium">{getScoreLabel(topicScore.impact)}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col items-center gap-2">
                            <select
                              value={topicScore.financial || ''}
                              onChange={(e) => handleScoreChange(topic.id, 'financial', e.target.value)}
                              className={`w-32 px-3 py-2 border-2 rounded-lg font-medium ${
                                topicScore.financial ? getScoreColor(topicScore.financial) : 'border-gray-300'
                              }`}
                            >
                              <option value="">Not Rated</option>
                              <option value="1">1 - Very Low</option>
                              <option value="2">2 - Low</option>
                              <option value="3">3 - Medium</option>
                              <option value="4">4 - High</option>
                              <option value="5">5 - Very High</option>
                            </select>
                            {topicScore.financial > 0 && (
                              <span className="text-xs font-medium">{getScoreLabel(topicScore.financial)}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-xs text-gray-600 space-y-1">
                            <div className="font-mono bg-gray-100 px-2 py-1 rounded">{topic.gri_code}</div>
                            <div className="font-mono bg-blue-100 px-2 py-1 rounded text-blue-700">{topic.esrs_code}</div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Matrix View */}
      {activeView === 'matrix' && matrix && (
        <div className="space-y-6">
          {/* Quadrant Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
              <h4 className="font-semibold text-red-900 mb-1">High Priority</h4>
              <p className="text-3xl font-bold text-red-700">{matrix.quadrants?.high_priority || 0}</p>
              <p className="text-xs text-red-600 mt-1">Both Impact & Financial</p>
            </div>
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-1">Impact Material</h4>
              <p className="text-3xl font-bold text-blue-700">{matrix.quadrants?.impact_material || 0}</p>
              <p className="text-xs text-blue-600 mt-1">High Impact Only</p>
            </div>
            <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
              <h4 className="font-semibold text-orange-900 mb-1">Financially Material</h4>
              <p className="text-3xl font-bold text-orange-700">{matrix.quadrants?.financially_material || 0}</p>
              <p className="text-xs text-orange-600 mt-1">High Financial Only</p>
            </div>
            <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-1">Not Material</h4>
              <p className="text-3xl font-bold text-gray-700">{matrix.quadrants?.not_material || 0}</p>
              <p className="text-xs text-gray-600 mt-1">Low on Both Axes</p>
            </div>
          </div>

          {/* Materiality Matrix Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-6">Double Materiality Matrix</h3>
            
            {/* Matrix Grid */}
            <div className="relative" style={{ height: '500px' }}>
              {/* Grid background */}
              <div className="absolute inset-0 border-2 border-gray-300 rounded-lg">
                {/* Quadrant dividers */}
                <div className="absolute top-1/2 left-0 right-0 border-t-2 border-gray-400"></div>
                <div className="absolute left-1/2 top-0 bottom-0 border-l-2 border-gray-400"></div>
                
                {/* Quadrant labels */}
                <div className="absolute top-2 left-2 text-xs text-gray-500">Low Impact, Low Financial</div>
                <div className="absolute top-2 right-2 text-xs text-gray-500">Low Impact, High Financial</div>
                <div className="absolute bottom-2 left-2 text-xs text-gray-500">High Impact, Low Financial</div>
                <div className="absolute bottom-2 right-2 text-xs font-semibold text-red-600">BOTH MATERIAL</div>
                
                {/* Axis labels */}
                <div className="absolute bottom-[-30px] left-1/2 transform -translate-x-1/2 text-sm font-semibold text-gray-700">
                  Financial Impact on Company →
                </div>
                <div className="absolute left-[-120px] top-1/2 transform -translate-y-1/2 -rotate-90 text-sm font-semibold text-gray-700">
                  Impact on Society/Environment →
                </div>
                
                {/* Data points */}
                {matrix.matrix_data?.map((point, i) => {
                  const x = (point.financial_score / 5) * 100;
                  const y = 100 - (point.impact_score / 5) * 100; // Invert Y axis
                  
                  return (
                    <div
                      key={i}
                      className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
                      style={{ left: `${x}%`, top: `${y}%` }}
                    >
                      <div className={`w-3 h-3 rounded-full ${
                        point.category === 'Environmental' ? 'bg-green-600' :
                        point.category === 'Social' ? 'bg-blue-600' :
                        'bg-purple-600'
                      }`}></div>
                      
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                        <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap">
                          <div className="font-semibold">{point.topic_name}</div>
                          <div className="text-gray-300">Impact: {point.impact_score} | Financial: {point.financial_score}</div>
                          <div className="text-yellow-300">{point.quadrant}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-12">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-600"></div>
                <span className="text-sm text-gray-700">Environmental</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                <span className="text-sm text-gray-700">Social</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-600"></div>
                <span className="text-sm text-gray-700">Governance</span>
              </div>
            </div>
          </div>

          {/* Material Topics List */}
          {matrix.matrix_data && matrix.matrix_data.filter(m => m.quadrant === "High Priority (Both Material)").length > 0 && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-red-900 mb-4">High Priority Material Topics</h3>
              <div className="space-y-2">
                {matrix.matrix_data
                  .filter(m => m.quadrant === "High Priority (Both Material)")
                  .map((topic, i) => (
                    <div key={i} className="bg-white border border-red-200 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getCategoryIcon(topic.category)}
                        <span className="font-semibold text-gray-900">{topic.topic_name}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-600">Impact: <strong>{topic.impact_score}</strong></span>
                        <span className="text-gray-600">Financial: <strong>{topic.financial_score}</strong></span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">CSRD Double Materiality Requirement</p>
            <p>
              Rate each topic on two dimensions: (1) <strong>Impact Materiality</strong> - how your operations affect society/environment, 
              and (2) <strong>Financial Materiality</strong> - how sustainability issues affect your financial performance. 
              Topics scoring ≥3 on either axis are considered material and require disclosure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GRICSRDModule;
